import { useHandler } from "@redotech/react-util/hook";
import { NumericCompareOperator } from "@redotech/redo-model/segments/segment-where-condition";
import { memo } from "react";
import { RedoTextInput } from "../../../arbiter-components/input/redo-text-input";
import { RedoListItem } from "../../../arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "../../../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import * as segmentBuilderCss from "../segment-builder.module.css";
import { NumericCompareInProgress } from "../segment-in-progress";

const numericCompareOperatorToFriendly: Record<NumericCompareOperator, string> =
  {
    [NumericCompareOperator.EQ]: "equals",
    [NumericCompareOperator.GT]: "is greater than",
    [NumericCompareOperator.LT]: "is less than",
    [NumericCompareOperator.GTE]: "is at least",
    [NumericCompareOperator.LTE]: "is at most",
    [NumericCompareOperator.NEQ]: "does not equal",
  };

const numericCompareOperatorOptions: RedoListItem<NumericCompareOperator>[] =
  Object.values(NumericCompareOperator).map((operator) => ({
    id: operator,
    type: "text",
    text: numericCompareOperatorToFriendly[operator],
    value: operator,
  }));

export const NumericFilter = memo(function NumericFilter({
  comparison,
  setComparison,
}: {
  comparison: NumericCompareInProgress;
  setComparison: (condition: NumericCompareInProgress) => void;
}) {
  return (
    <>
      <RedoSingleSelectDropdownInput<NumericCompareOperator>
        fitToAnchor={false}
        flexGrow={false}
        options={numericCompareOperatorOptions}
        optionSelected={({ item }) => {
          setComparison({ ...comparison, operator: item.value });
        }}
        selectedItem={numericCompareOperatorOptions.find(
          (o) => o.value === comparison.operator,
        )}
        size="xs"
      />
      <NumericFilterInput
        dataTestId="numeric-filter-input"
        setValue={(value) => {
          setComparison({ ...comparison, value });
        }}
        value={comparison.value}
      />
    </>
  );
});

export const NumericFilterInput = memo(function NumericFilterInput({
  value,
  setValue,
  maxDigits,
  allowNegative,
  dataTestId,
}: {
  value: number | undefined;
  setValue: (value: number | undefined) => void;
  maxDigits?: number;
  allowNegative?: boolean;
  dataTestId?: string;
}) {
  const pattern = allowNegative ? "-?\\d*" : "\\d*";

  const setNumber = useHandler((value: string) => {
    const numericValue = parseInt(value);
    if (isNaN(numericValue)) {
      setValue(undefined);
    } else {
      setValue(numericValue);
    }
  });
  return (
    <RedoTextInput
      className={segmentBuilderCss.relativeUnitValueSelector}
      constrainToInputLength
      dataTestId={dataTestId}
      htmlPattern={pattern}
      maxLength={maxDigits}
      setValue={setNumber}
      value={`${value ?? ""}`}
    />
  );
});
